import { useState } from 'react'
import { Box, Flex, toaster } from '@minetrace/ui'
import { LuDownload as Download } from 'react-icons/lu'
import { modificationRequestService, proveedoresService } from '../api/services'
import { parseStagedFileRef } from '../lib/modificationStaging'
import { downloadAuthenticated } from '../pages/Expedientes/ExpedienteElectronico/helpers'
import { brand } from '../theme'

interface DocumentoDescargaProps {
  /**
   * QUE es el documento: "Contrato 2026", "Documento IGAFOM/IGAC", "Cargo de
   * Presentación". Es lo unico que se muestra.
   */
  caption: string
  /** objectName en el bucket. Se ignora si se pasa `descargar`. */
  storagePath?: string
  /**
   * Descarga alternativa, para lo que no sale del bucket de documentos por la ruta
   * comun: hoy solo el certificado CUID, que tiene su propio endpoint.
   */
  descargar?: () => Promise<void>
  /**
   * Nombre real del archivo. NO se muestra: va en el tooltip y es el nombre con el
   * que se guarda en el disco del usuario.
   */
  fileName?: string | null
  variant?: 'pill' | 'link'
  size?: 'sm' | 'xs'
}

/**
 * Boton para bajar un documento del expediente del minero.
 *
 * Muestra el TIPO de documento, no el nombre del archivo. Los nombres los pone
 * quien escanea: conviven "reporte-tributario-2026.pdf" con "scan_20250830_00147.pdf",
 * y al usarlos como texto del enlace la fila se descuadra y no se entiende que es
 * cada cosa. El nombre real sigue estando en el tooltip y es el que se usa al
 * guardar el archivo.
 *
 * Reemplaza a nueve copias del mismo enlace repartidas por el wizard y el detalle.
 * Todas compartian el mismo defecto: la descarga fallaba sin decir nada — en el
 * detalle con un `return` mudo y en el wizard con una promesa rechazada que nadie
 * atrapaba.
 */
export default function DocumentoDescarga({
  caption,
  storagePath,
  descargar,
  fileName,
  variant = 'link',
  size = 'sm',
}: DocumentoDescargaProps) {
  const [descargando, setDescargando] = useState(false)

  const handleClick = async () => {
    if (descargando) return
    setDescargando(true)
    try {
      const staged = parseStagedFileRef(storagePath)
      if (descargar) await descargar()
      else if (staged) await downloadAuthenticated(modificationRequestService.getStagedFileDownloadUrl(staged[0], staged[1]), fileName ?? 'documento.pdf')
      else if (storagePath) await proveedoresService.downloadFile(storagePath, fileName ?? undefined)
    } catch {
      toaster.error({ description: `No se pudo descargar ${caption}. El archivo no está disponible.` })
    } finally {
      setDescargando(false)
    }
  }

  const estiloPill = {
    px: 3,
    py: 2,
    borderRadius: 'lg',
    bg: `${brand.green}10`,
    color: brand.green,
    fontWeight: '600',
    _hover: descargando ? {} : { bg: `${brand.green}20` },
  } as const

  const estiloLink = {
    color: brand.green,
    fontWeight: '500',
    _hover: descargando ? {} : { textDecoration: 'underline' },
  } as const

  return (
    <Flex
      as="button"
      {...{ type: 'button', disabled: descargando }}
      onClick={handleClick}
      title={fileName ? `${caption} — ${fileName}` : caption}
      display="inline-flex"
      alignItems="center"
      gap={1.5}
      fontSize={size}
      cursor={descargando ? 'default' : 'pointer'}
      opacity={descargando ? 0.6 : 1}
      transition="all 0.15s"
      w="fit-content"
      maxW="100%"
      {...(variant === 'pill' ? estiloPill : estiloLink)}
    >
      <Download size={size === 'xs' ? 12 : 14} />
      <Box as="span" truncate>{descargando ? 'Descargando...' : caption}</Box>
    </Flex>
  )
}
